'use client';

import React, { useCallback, useId } from 'react';
import { useTranslations } from 'use-intl';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { Tooltip } from 'react-tooltip';
import Image from '@/components/atoms/Image';
import Link from '@/components/atoms/link';
import QuantityWidget from '@/components/atoms/quantity-widget';
import useFormat from '@/hooks/useFormat';
import useOptimisticQuantity from '@/hooks/useOptimisticQuantity';
import { Product } from '@/types/entity/product';
import { pushRemoveFromCart } from '@/lib/analytics/dataLayer';
import { classnames } from '@/utils/classnames/classnames';
import { resolveStockStatus } from '@/utils/product/resolve-stock-status';
import { isLineOverAvailableStock } from '@/utils/isBackorderRestricted';
import { useRegisterCartFlush } from '../../cart-flush-registry';
import { CartItemsTableProps } from './types';

// ─── CartItemRow ──────────────────────────────────────────────────────────────

type CartItemRowProps = {
  item: Product & { deleted?: boolean };
  isViewOnly?: boolean;
  overStockQuoteUrl?: string;
  onUpdateQuantity: (id: string, qty: number) => Promise<void>;
  onRemove: (id: string) => Promise<void>;
};

const CartItemRow = ({ item, isViewOnly, overStockQuoteUrl, onUpdateQuantity, onRemove }: CartItemRowProps) => {
  const translate = useTranslations();
  const { formatCurrency } = useFormat();
  const tooltipId = useId();
  // EWM-612 — this row no longer reads the mutation lock. A structural cart write
  // (remove / clear) surfaces the processing modal, which blocks interaction; the row
  // is gated only by view-only. Quantity edits are optimistic + per-item (EWM-610).
  const locked = !!isViewOnly;

  // EWM-545 — same label logic as PDP/PLP: no-stock + new/pending (N/P) →
  // "Stock due soon"; otherwise the regular in-stock / out-of-stock label.
  const stockStatus = resolveStockStatus({
    inStock: item.inStock,
    isStockDueSoonEligible: item.isStockDueSoonEligible,
  });

  const pushRemoveAnalytics = useCallback(() => {
    pushRemoveFromCart({
      currency: item.currency,
      items: [
        {
          item_id: item.sku ?? item.id,
          item_name: item.name,
          price: item.price,
          quantity: item.quantity ?? 1,
          item_brand: item.brand,
        },
      ],
    });
  }, [item]);

  // EWM-610 — optimistic per-item quantity: instant local value, a trailing-debounced
  // commit of the FINAL qty (rapid +/- → one write), supersede of in-flight writes,
  // and qty 0 → remove. Replaces the bespoke localQty/useDebounce/confirmedQty block.
  const {
    displayQty,
    isPending: isQtyPending,
    setQty,
    flush,
  } = useOptimisticQuantity({
    serverQty: item.quantity ?? 1,
    onCommit: (qty) => onUpdateQuantity(item.id, qty),
    onRemove: () => {
      pushRemoveAnalytics();
      return onRemove(item.id);
    },
    removeAtOrBelow: 0,
  });

  // EWM-613 — register this row's flush so Proceed-to-Checkout can commit a pending
  // debounced quantity before navigating to /checkout (no "checkout used my old qty").
  useRegisterCartFlush(item.id, flush);

  const handleRemoveClick = useCallback(() => {
    onRemove(item.id);
    pushRemoveAnalytics();
  }, [item.id, onRemove, pushRemoveAnalytics]);

  // EWM-598 — per-line product discount (was/now). When the line carries a
  // product discount, a free-gift ($0) or a reduced bundle cost, `discountedPrice`
  // is the price CT actually charges ("now"); `item.price` is the original
  // ("was"). Mirrors the PDP/PLP treatment (Phases 1–3): discounted price
  // prominent, original struck behind a visually-hidden "Was" label so screen
  // readers don't read it as the price to pay. `!= null` keeps a legitimate $0
  // free gift in scope (a falsy check would treat it as "no discount").
  const isLineDiscounted = typeof item.discountedPrice === 'number' && item.discountedPrice < item.price;
  const effectiveUnitPrice = isLineDiscounted ? (item.discountedPrice as number) : item.price;
  const priceEach = formatCurrency(effectiveUnitPrice, item.currency);
  // The struck price: the product-discount original takes precedence; otherwise
  // fall back to RRP (>channel price), consistent with the PDP pricing card.
  const wasPrice = isLineDiscounted ? item.price : item.rrp && item.rrp > item.price ? item.rrp : undefined;
  // EWM-598 — accent saving label, same shape as the PDP pricing card.
  const savingAmount = isLineDiscounted ? item.price - effectiveUnitPrice : 0;
  const savingPercent = isLineDiscounted && item.price > 0 ? Math.round((savingAmount / item.price) * 100) : 0;
  const isFreeGift = item.isGift || effectiveUnitPrice === 0;
  const savingLabel = isLineDiscounted
    ? isFreeGift
      ? translate('cart.free-gift')
      : savingPercent > 0
        ? translate('pdp.percent-off', { percent: savingPercent })
        : translate('pdp.save-amount', { amount: formatCurrency(savingAmount, item.currency) })
    : undefined;
  // EWM-549 — per-line Quantity Value Break (QVB / bulk) saving. `item.price` is
  // already the applied tier price for this line (EWM-523); `qvbBaseUnitPrice` is
  // the customer's qty-1 tier/contract price. Saving = (base − applied) × qty,
  // pre-tax, tracking the optimistic `displayQty` so it recalculates on every
  // quantity change. Suppressed on product-discounted lines so it doesn't stack
  // with the product-discount saving label above.
  const qvbBase = item.qvbBaseUnitPrice;
  const qvbSavingAmount =
    !isLineDiscounted && typeof qvbBase === 'number' && qvbBase > item.price
      ? Math.round((qvbBase - item.price) * displayQty * 100) / 100
      : 0;
  const qvbSavingLabel =
    qvbSavingAmount > 0
      ? translate('cart.qvb-saving', { amount: formatCurrency(qvbSavingAmount, item.currency) })
      : undefined;
  const lineTotal = formatCurrency(effectiveUnitPrice * displayQty, item.currency);
  const removeLabel = translate('cart.remove-item-label', { name: item.name });
  const viewOnlyTip = translate('cart.view-only-action');

  const productBlock = (imgSize: 'sm' | 'md') => (
    <div className="flex items-start gap-5">
      <Link href={item.url ?? '#'} className="shrink-0">
        <div className={`relative bg-gray-50 ${imgSize === 'md' ? 'size-20' : 'size-[72px]'}`}>
          <Image src={item.images?.[0]} alt={item.name} fill style={{ objectFit: 'contain' }} loading="lazy" />
        </div>
      </Link>
      <div className="flex min-w-0 flex-col gap-1">
        <Link href={item.url ?? '#'} className="text-14 leading-snug font-semibold text-gray-800 hover:underline">
          {item.name}
        </Link>
        {(item.sku || item.packSize) && (
          <div className="text-12 flex items-center gap-3 text-gray-500">
            {item.sku && (
              <span>
                {translate('common.cat-no')}: <span className="font-semibold text-gray-700">{item.sku}</span>
              </span>
            )}
            {item.packSize && (
              <span>
                {translate('cart.pack-size-prefix')}{' '}
                <span className="font-semibold text-gray-700">{item.packSize}</span>
              </span>
            )}
          </div>
        )}
        <div className="mt-0.5 flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-13 text-primary font-semibold">
            {translate('cart.price-each', { price: priceEach })}
          </span>
          {wasPrice !== undefined && (
            // `<s>` marks the original as no-longer-current; the visually hidden
            // label keeps screen readers from reading it as the price to pay.
            <s className="text-13 text-gray-400" data-testid="was-price">
              <span className="sr-only">{translate('pdp.was-price')} </span>
              {formatCurrency(wasPrice, item.currency)}
            </s>
          )}
          {savingLabel && (
            <span className="text-12 font-semibold text-red-600" data-testid="line-saving">
              {savingLabel}
            </span>
          )}
          {qvbSavingLabel && (
            <span className="text-12 font-semibold text-green-600" data-testid="qvb-saving">
              {qvbSavingLabel}
            </span>
          )}
        </div>
        {/* EWM-545 — match the PDP/PLP stock label: a new/pending (N/P) product
            with no stock reads "Stock due soon" instead of "Out of Stock" (same
            red styling). Most visible for back-order customers, who can add such
            a product to the cart and previously saw a mismatched "Out of Stock". */}
        <span
          className={`text-12 mt-1 font-semibold ${stockStatus === 'in-stock' ? 'text-green-600' : 'text-red-600'}`}
          data-testid="stock-badge"
        >
          {translate(
            stockStatus === 'in-stock'
              ? 'cart.stock-in-stock'
              : stockStatus === 'due-soon'
                ? 'cart.stock-due-soon'
                : 'cart.stock-out-of-stock',
          )}
        </span>
      </div>
    </div>
  );

  const stepperBlock = (suffix: string, wrapperClassName?: string) => (
    <span
      data-testid={`stepper-wrapper-${suffix}`}
      data-tooltip-id={isViewOnly ? `${tooltipId}-qty-${suffix}` : undefined}
      data-tooltip-content={isViewOnly ? viewOnlyTip : undefined}
      aria-busy={isQtyPending || undefined}
      className={classnames('inline-block', wrapperClassName)}
    >
      {/* EWM-641 — no stock-based cap on the stepper (that would leak warehouse
          stock levels — Luke 2026-07-03). Restricted customers are gated at the cart
          (Proceed-disable + over-stock banner) and at checkout; the BFF checkout
          gate is the authoritative backstop. */}
      <QuantityWidget
        value={displayQty}
        minValue={0}
        onChange={setQty}
        disabled={locked}
        showLabel={false}
        spinbutton
        variant="primary"
      />
      {isViewOnly && (
        <Tooltip id={`${tooltipId}-qty-${suffix}`} place="top" className="text-12 rounded-md bg-gray-700 text-white" />
      )}
    </span>
  );

  const removeBlock = (suffix: string) => (
    <span
      data-tooltip-id={isViewOnly ? `${tooltipId}-rm-${suffix}` : undefined}
      data-tooltip-content={isViewOnly ? viewOnlyTip : undefined}
      className="inline-block"
    >
      <button
        type="button"
        aria-label={removeLabel}
        onClick={handleRemoveClick}
        disabled={locked}
        className="rounded p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40"
      >
        <XMarkIcon className="size-5" aria-hidden />
      </button>
      {isViewOnly && (
        <Tooltip id={`${tooltipId}-rm-${suffix}`} place="top" className="text-12 rounded-md bg-gray-700 text-white" />
      )}
    </span>
  );

  const moqTriggered = item.minimumOrderQuantity !== undefined && (item.quantity ?? 0) < item.minimumOrderQuantity;

  // EWM-641 — restricted customers (overStockQuoteUrl set) see a per-line error +
  // quote link on any line whose quantity exceeds available stock. Unrestricted
  // customers (url undefined) back-order freely, so no error shows.
  const overStock = !!overStockQuoteUrl && isLineOverAvailableStock(item);

  return (
    <div className={moqTriggered || overStock ? undefined : 'border-b border-gray-200'}>
      {/* Desktop row — 4-col CSS grid, hidden below md */}
      <div
        data-testid="cart-item-desktop"
        className="hidden grid-cols-[1fr_11rem_9rem_3.5rem] items-start transition-colors hover:bg-gray-50 md:grid"
      >
        <div className="p-5">{productBlock('md')}</div>
        <div className="flex items-center justify-center p-5">{stepperBlock('d')}</div>
        <div className="flex items-center justify-end py-5 pr-2 pl-5">
          <span className="text-18 text-primary font-bold whitespace-nowrap">{lineTotal}</span>
        </div>
        <div className="flex items-center justify-center py-5 pr-5 pl-2">{removeBlock('d')}</div>
      </div>

      {/* Mobile card — visible below md */}
      <div data-testid="cart-item-mobile" className="p-4 md:hidden">
        {productBlock('sm')}
        <div className="mt-3 flex items-center justify-between">
          {/* [&_input]:w-16 must stay as a complete literal — Tailwind JIT scans for full class strings.
              Sized to fit up to 4-digit quantities (e.g. 9999) without clipping on mobile (EWM-470). */}
          {stepperBlock('m', '[&_input]:w-16')}
          <div className="flex items-center gap-3">
            <span className="text-18 text-primary font-bold">{lineTotal}</span>
            {removeBlock('m')}
          </div>
        </div>
      </div>

      {moqTriggered && (
        <div role="alert" aria-atomic="true" className="border-b border-gray-200 px-5 pb-5">
          <div className="flex items-center gap-2 rounded border border-red-700 bg-red-50 px-3 py-2">
            <ExclamationTriangleIcon className="size-4 shrink-0 text-red-700" aria-hidden="true" />
            <span className="text-12 font-medium text-red-700">
              {translate('cart.moq-warning', { moq: item.minimumOrderQuantity! })}
            </span>
          </div>
        </div>
      )}

      {overStock && (
        <div role="alert" aria-atomic="true" className="border-b border-gray-200 px-5 pb-5">
          <div className="flex items-center gap-2 rounded border border-red-700 bg-red-50 px-3 py-2">
            <ExclamationTriangleIcon className="size-4 shrink-0 text-red-700" aria-hidden="true" />
            <span className="text-12 font-medium text-red-700">
              {translate.rich('cart.line-over-stock', {
                link: (chunks) => (
                  <a
                    href={overStockQuoteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold underline"
                  >
                    {chunks}
                  </a>
                ),
              })}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── CartItemsTable ────────────────────────────────────────────────────────────

const CartItemsTable = ({ items, isViewOnly, overStockQuoteUrl, onUpdateQuantity, onRemove }: CartItemsTableProps) => {
  const translate = useTranslations();
  const visibleItems = items.filter((item) => !item.deleted);

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      {/* Desktop table header — hidden on mobile */}
      <div className="hidden grid-cols-[1fr_11rem_9rem_3.5rem] border-b border-gray-200 md:grid">
        <div className="text-14 p-5 font-bold text-gray-800">{translate('cart.header-product')}</div>
        <div className="text-14 p-5 text-center font-bold text-gray-800">{translate('cart.header-qty')}</div>
        <div className="text-14 p-5 text-right font-bold text-gray-800">{translate('cart.header-total')}</div>
        <div className="p-5" />
      </div>

      {visibleItems.map((item) => (
        <CartItemRow
          key={item.id}
          item={item}
          isViewOnly={isViewOnly}
          overStockQuoteUrl={overStockQuoteUrl}
          onUpdateQuantity={onUpdateQuantity}
          onRemove={onRemove}
        />
      ))}
    </div>
  );
};

export default CartItemsTable;
